import {
  NativeModules,
  StyleSheet,
  Text,
  View,
  SafeAreaView,
  ScrollView,
  Dimensions,
} from 'react-native';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import { wrapPromiseInTimeout } from 'fitbeat.gym-core';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import { ConnectingLoaderIcon } from '../../ui/components/Icons';
import { showError } from '../../ui/components/Toast';
import NetworkTile from './components/NetworkTile';
const { width } = Dimensions.get('window');
import { ExclamationIcon } from '../../ui/components/Icons';
import { Logger } from '../../utils';

const WAIT_CONNECTION_TIMEOUT = 5000;
const BASIC_CONNECTION_TIMEOUT = 500;
const STATE_REFRESH_TIMEOUT = 5000;
const SCAN_TIMEOUT = 30000; // scanning is throttled: https://developer.android.com/guide/topics/connectivity/wifi-scan#wifi-scan-throttling

class WifiListScreen extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isScanningForWifi: true,
      nextAllowedScanTime: 0,
      networkList: [],
      connection: null,
    };
  }

  componentDidMount = () => {
    this.scanForNetworks();
  }

  componentWillUnmount = () => {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  getConnectionInfo = async (previous) => {
    if (previous && previous.hasInternet) {
      Logger.debug('wifi: using existing connection state');
      return previous;
    }

    Logger.debug('wifi: getting connection state');
    return await NativeModules.EliteWiFi.waitForInternetConnection(WAIT_CONNECTION_TIMEOUT);
  }

  updateConnectionDetails = async (initialConnectionState) => {
    try {
      if (initialConnectionState) {
        this.setState({
          connection: initialConnectionState, // show previous state while loading new
        });
      } else if (!this.state.connection) {
        Logger.debug('wifi: getting basic network info');
        const basicConnection = await NativeModules.EliteWiFi.waitForInternetConnection(BASIC_CONNECTION_TIMEOUT);
        return this.updateConnectionDetails(basicConnection);
      }

      const connection = await this.getConnectionInfo(initialConnectionState);
      this.setState({
        connection,
      });

      if (connection.hasInternet) {
        const { navigation } = this.props;
        const navigationSource = navigation.getParam('navigationSource');
        if (navigationSource === 'ProfileScreen') {
          // no-op (stay on wifi list when changing wifi setup)
        } else if (navigationSource === undefined) {
          // todo: try to pass 'EliteSetup' explicitly via params into nested navigators
          // from elite setup/wifi checks
          Logger.leaveBreadcrumb('connected with internet');
          navigation.dangerouslyGetParent().navigate('Loading');
          return true;
        }
      }
    } catch (e) {
      this.setState({ connection: null });
    }
  }

  refreshNetworkList = async () /* todo: add types https://gitlab.com/fitbeat/fitbeat/-/issues/1862 */ => {
    if (this.state.nextAllowedScanTime > Date.now()) {
      Logger.debug(`wifi: recently scanned, not scanning until ${this.state.nextAllowedScanTime}`);
      return {
        message: 'skipped',
        networks: this.state.networkList,
      };
    }

    this.setState({ isScanningForWifi: true });
    return await NativeModules.EliteWiFi.scan();
  }

  scanForNetworks = async () => {
    const scanPromise = this.refreshNetworkList();
    const shouldSkipScanningBecauseItGotConnected = await this.updateConnectionDetails();
    if (shouldSkipScanningBecauseItGotConnected) {
      return;
    }

    try {
      const result = await scanPromise;
      Logger.debug(`wifi: scan results ${result.message}`);

      const { connection } = this.state;
      const filterOutSsid = connection && connection.network ? connection.network.ssid : undefined;
      const networkList = result.networks.filter(f => f.ssid !== filterOutSsid);

      this.setState({
        networkList,
        isScanningForWifi: false,
        nextAllowedScanTime: result.message === 'skipped' ? this.state.nextAllowedScanTime : Date.now() + SCAN_TIMEOUT,
      });
    } catch (e) {
      showError((<Text>{e.message}</Text>));
      this.setState({
        isScanningForWifi: false,
      });
    }

    this.timeoutId = setTimeout(() => {
      this.scanForNetworks();
      this.timeoutId = undefined;
    }, STATE_REFRESH_TIMEOUT);
  }

  forgetNetwork = async () => {
    await NativeModules.EliteWiFi.forgetNetworks();
    this.setState({ connection: null });
  }

  render() {
    const { t, navigation } = this.props;
    const { isScanningForWifi, networkList, connection } = this.state;
    const hasActiveConnection = connection && connection.network;
    const showScanning = isScanningForWifi && (!hasActiveConnection || networkList.length === 0);

    return (
      <SafeAreaView style={styles.container}>
        {!hasActiveConnection && (
          <View style={styles.headerTextContainer}>
            <Text style={styles.headerText}>{t('connectToWifiToUse')}</Text>
          </View>
        )}

        {hasActiveConnection && (
          <View style={{ width: width, paddingBottom: defaultScale(30) }}>
            <View style={styles.headerTextContainer}>
              <Text style={styles.headerText}>{t('connectedWifiNetwork')}</Text>
            </View>
            <NetworkTile
              isActiveConnection
              ssid={connection.network.ssid}
              strength={connection.network.strength}
              isProtected={connection.network.isProtected}
              hasInternet={connection.hasInternet}
              handleForgetNetwork={this.forgetNetwork}
              t={t}
              navigation={navigation}
            />
          </View>
        )}

        {showScanning && (
          <View style={styles.loadingContainer}>
            <Text style={styles.subText}>
              {isScanningForWifi ? t('scanningWifi') : t('scanned')}
            </Text>
            <View style={styles.loadingIcon}>
              <ConnectingLoaderIcon
                useClockwiseSquareSvg
                width={defaultScale(70)}
                height={defaultScale(70)}
                animating={isScanningForWifi}
                color={Theme.BLACK}
              />
            </View>
          </View>
        )}
        {networkList.length === 0 && !isScanningForWifi && !hasActiveConnection && (
          <View style={styles.scanResultsWarningContainer}>
            <View style={styles.warningTextHeaderContainer}>
              <ExclamationIcon
                size={defaultScale(40)}
                color={Theme.DARK_CHARCOAL}
                borderColor={Theme.DARK_CHARCOAL}
              />
              <Text style={styles.warningTextHeader}>{t('noWifFound')}</Text>
            </View>
            <Text style={styles.warningSubText}>{t('scanWillResume')}</Text>
          </View>
        )}
        {networkList.length > 0 && (
          <ScrollView style={styles.wifiListContainer}>
            {hasActiveConnection && (
              <Text style={styles.subText}>
                {t('otherAvailableNetworks')}
              </Text>
            )}
            {networkList.map((item, index) => (
              <NetworkTile
                ssid={item.ssid}
                strength={item.strength}
                isProtected={item.isProtected}
                isLastItem={index + 1 === networkList.length}
                afterNetworkAdded={this.updateConnectionDetails}
                t={t}
                navigation={navigation}
              />
            ))}
          </ScrollView>
        )}
      </SafeAreaView>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: Theme.WHITE,
  },
  loadingContainer: {
    alignItems: 'center',
  },
  scanResultsWarningContainer: {
    paddingVertical: defaultScale(30),
  },
  headerTextContainer: {
    width: '100%',
    paddingHorizontal: defaultScale(10),
    paddingVertical: defaultScale(30),
  },
  headerText: {
    ...Theme.textStyle.heading,
    textAlign: 'center',
  },
  subText: {
    ...Theme.textStyle.headingReg,
    paddingVertical: defaultScale(10),
  },
  warningTextHeaderContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  warningTextHeader: {
    ...Theme.textStyle.heading2,
    paddingHorizontal : defaultScale(10),
  },
  warningSubText: {
    ...Theme.textStyle.bodyCopyReg,
    alignSelf: 'center',
    paddingStart : defaultScale(25),  // half of (image size + warningTextHeader.paddingHorizontal)
  },
  subText: {
    ...Theme.textStyle.bodyCopyReg,
    alignSelf: 'center',
  },
  loadingIcon: {
    paddingVertical: defaultScale(10),
  },
  wifiListContainer: {
    width: width,
    paddingVertical: defaultScale(20),
  },
});

export default translate(['elite'], { wait: true })(
  WifiListScreen,
);
